import { useEffect, useState } from 'react';
import { CalendarRange } from 'lucide-react';
import type { FilterState } from '../dashboardFilterUtils';
import {
  MAX_NEXT_WEEKS, forwardCostPeriod, getAllFutureWeeksRange, getNextWeeksRange,
} from '../costTabFilters';
import { FilterSection, FilterField, filterControlClass } from '../../filters/FilterSection';

const QUICK_RANGES: Array<{ key: string; label: string }> = [
  { key: 'last12Months', label: 'Last 12 months' },
  { key: 'thisMonth', label: 'This month (live)' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'lastQuarter', label: 'Last quarter' },
  { key: 'thisYear', label: 'This year' },
  { key: 'lastYear', label: 'Last year' },
];

// CR-5454: the Cost tab's default and only forward-looking range.
const NEXT_20_WEEKS_RANGE = { key: 'next20Weeks', label: 'Next 20 weeks' };

interface DateRangeSectionProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  activeQuickRange: string | null;
  isLast12MonthsActive: boolean;
  setQuickDateRange: (range: string) => void;
  /** CR-4800: compliance data has no historical-vs-live split. */
  showDataMode: boolean;
  /** CR-5454: offer "Next 20 weeks" — the Cost tab only. */
  showNext20Weeks?: boolean;
  /** CR-8611: quick ranges that may be picked; the rest are shown disabled. Omitted = all. */
  allowedRanges?: string[];
  /** CR-8611: tooltip on a disabled range. */
  disabledHint?: string;
  /** CR-8611: limits for the From/To pickers (the Cost sub-tab's direction). */
  dateBounds?: { min?: string; max?: string };
}

/**
 * CR-6401: DATE RANGE group — quick-range presets, the explicit From/To dates,
 * and the Historical/Live data toggle that previously sat stranded at the
 * bottom of the panel next to the strategic-suppliers tickbox.
 */
export function DateRangeSection({
  filters,
  setFilters,
  activeQuickRange,
  isLast12MonthsActive,
  setQuickDateRange,
  showDataMode,
  showNext20Weeks = false,
  allowedRanges,
  disabledHint,
  dateBounds,
}: DateRangeSectionProps) {
  const isAllowed = (key: string) => !allowedRanges || allowedRanges.includes(key);
  // Next N weeks and All future weeks look forward, like Next 20 weeks: offered
  // wherever it is (CR-8611 greys all three out on the Historical sub-tab).
  const forwardAllowed = isAllowed(NEXT_20_WEEKS_RANGE.key);
  const ranges = showNext20Weeks ? [NEXT_20_WEEKS_RANGE, ...QUICK_RANGES] : QUICK_RANGES;
  // CR-1421 decision 2 (Aaron, 15 Oct 2026): legacy's "Next N weeks" and "All
  // future weeks" beside Next 20 weeks, on the Cost tab only.
  const forward = showNext20Weeks ? forwardCostPeriod(filters) : null;
  const nextN = forward?.kind === 'next' && forward.weeks !== 20 ? forward.weeks : null;
  const [weeksText, setWeeksText] = useState(nextN ? String(nextN) : '');
  useEffect(() => { if (nextN) setWeeksText(String(nextN)); }, [nextN]);
  const applyWeeks = (raw: string) => {
    setWeeksText(raw);
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1 || n > MAX_NEXT_WEEKS) return;
    setFilters({ ...filters, ...getNextWeeksRange(n), dataMode: 'historical' });
  };
  const pill = (active: boolean) => `px-4 py-2 text-sm font-medium border rounded-md transition-colors ${
    active
      ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
      : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
  }`;
  const isRangeActive = (key: string) =>
    key === 'last12Months' ? isLast12MonthsActive : activeQuickRange === key;

  return (
    <FilterSection title="Date Range" icon={CalendarRange}>
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex flex-wrap gap-2 max-w-2xl">
          {ranges.slice(0, showNext20Weeks ? 1 : 0).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { if (isAllowed(key)) setQuickDateRange(key); }}
              disabled={!isAllowed(key)}
              title={isAllowed(key) ? undefined : disabledHint}
              className={`px-4 py-2 text-sm font-medium border rounded-md transition-colors ${
                !isAllowed(key)
                  ? 'border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed'
                  : isRangeActive(key)
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                  : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
              }`}
            >
              {label}
            </button>
          ))}
          {showNext20Weeks && (
            <>
              <label
                className={forwardAllowed
                  ? `${pill(nextN !== null)} inline-flex items-center gap-2 cursor-text`
                  : 'px-4 py-2 text-sm font-medium border rounded-md border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed inline-flex items-center gap-2'}
                title={forwardAllowed ? undefined : disabledHint}
              >
                Next
                <input
                  type="number" min={1} max={MAX_NEXT_WEEKS} step={1} inputMode="numeric"
                  value={weeksText} onChange={(e) => applyWeeks(e.target.value)} disabled={!forwardAllowed}
                  aria-label="Number of weeks ahead" placeholder="N"
                  className="w-14 rounded border border-border bg-background px-1.5 py-0.5 text-sm text-foreground"
                />
                weeks
              </label>
              <button
                type="button"
                onClick={() => { if (forwardAllowed) setFilters({ ...filters, ...getAllFutureWeeksRange(), dataMode: 'historical' }); }}
                disabled={!forwardAllowed}
                title={forwardAllowed ? undefined : disabledHint}
                className={forwardAllowed ? pill(forward?.kind === 'all')
                  : 'px-4 py-2 text-sm font-medium border rounded-md border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed'}
              >
                All future weeks
              </button>
            </>
          )}
          {ranges.slice(showNext20Weeks ? 1 : 0).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { if (isAllowed(key)) setQuickDateRange(key); }}
              disabled={!isAllowed(key)}
              title={isAllowed(key) ? undefined : disabledHint}
              className={`px-4 py-2 text-sm font-medium border rounded-md transition-colors ${
                !isAllowed(key)
                  ? 'border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed'
                  : isRangeActive(key)
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                  : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="w-44">
            <FilterField label="From date">
              <input
                type="date"
                value={filters.dateFrom}
                max={dateBounds?.max}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className={filterControlClass}
              />
            </FilterField>
          </div>
          <div className="w-44">
            <FilterField label="To date">
              <input
                type="date"
                value={filters.dateTo}
                min={dateBounds?.min}
                max={dateBounds?.max}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className={filterControlClass}
              />
            </FilterField>
          </div>
        </div>
      </div>

      {showDataMode && (
        <>
        <div className="flex items-center gap-3 mt-4">
          <span className="text-sm font-medium text-foreground">Data</span>
          <div className="flex">
            <button
              onClick={() => {
                if (activeQuickRange !== 'thisMonth') setFilters({ ...filters, dataMode: 'historical' });
              }}
              disabled={activeQuickRange === 'thisMonth'}
              className={`px-4 py-2 text-sm font-medium rounded-l-md border transition-colors ${
                activeQuickRange === 'thisMonth'
                  ? 'bg-muted text-muted-foreground border-border cursor-not-allowed opacity-50'
                  : filters.dataMode === 'historical'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              Historical
            </button>
            <button
              onClick={() => setFilters({ ...filters, dataMode: 'live' })}
              className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-r border-b transition-colors ${
                filters.dataMode === 'live'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              Live
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Historical: past months use their frozen month-end arrears; a month with no snapshot shows no arrears. Live: every figure uses today&apos;s open overdue lines.
        </p>
        </>
      )}
    </FilterSection>
  );
}
